import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

import { rowIdFromTruck, VehicleRowGroup } from './adapter';

/** rowId から row を引く。D&D・詳細パネル・自動配車で共用する */
export function buildRowLookup(
  rowGroups: VehicleRowGroup[],
): Map<string, VehicleRowGroup['rows'][number]> {
  const map = new Map<string, VehicleRowGroup['rows'][number]>();
  rowGroups.forEach((g) => g.rows.forEach((r) => map.set(r.rowId, r)));
  return map;
}

/**
 * rowId → shift_truck_id (= delivery.id)。サーバ既存の delivery を対象。
 * - operations が populated な delivery: 全 cycle を登録
 * - operations が空のシフトのみ delivery: rotation=1 に対応させる
 *   （車両追加直後の空枠もそのトラックの 1 回転目 row として使われるため）
 */
export function buildRowIdToShiftTruckId(
  deliveries: PlanningsDeliveryWithLegRunEntity[],
): Map<string, number> {
  const m = new Map<string, number>();
  deliveries.forEach((d) => {
    if (d.operations.length === 0) {
      m.set(rowIdFromTruck(d.truckId, 1, d.id), d.id);
      return;
    }
    const cycles = new Set(d.operations.map((op) => (op.cycle >= 1 ? op.cycle : 1)));
    cycles.forEach((cycle) => {
      m.set(rowIdFromTruck(d.truckId, cycle, d.id), d.id);
    });
  });
  return m;
}

/**
 * legRun id → 非 compact な legRun 実体。
 * `/plannings/.../deliveries` の `shipmentOrderLegRuns[]` は compact=true でシリアライズされて
 * いるため、数量 / 備考 / 作業秒 / 指定時間 などの補完と、分割ダイアログの対象解決に使う。
 *
 * supplementalLegRuns は未割当一覧に入らない案件（宵積み）を別に引いたもの
 * (useQuerySupplementalLegRuns)。両方にある案件は一覧の方を使う。
 */
export function buildLegRunMaterialsMap(
  legRuns: readonly ShipmentOrderLegRunForPlanEntity[],
  supplementalLegRuns: readonly ShipmentOrderLegRunForPlanEntity[] = [],
): Map<number, ShipmentOrderLegRunForPlanEntity> {
  const m = new Map<number, ShipmentOrderLegRunForPlanEntity>();
  for (const lr of [...supplementalLegRuns, ...legRuns]) {
    if (lr.id != null) m.set(lr.id, lr);
  }
  return m;
}

/**
 * unassigned id (= `lr-{legRunId}`) → shipment_order_id。
 * 同じ shipment_order に複数 legRun があれば 1 つにまとまる。
 */
export function resolveShipmentOrderIdsFromUnassignedIds(
  ids: string[],
  legRuns: ShipmentOrderLegRunForPlanEntity[],
): number[] {
  const set = new Set<number>();
  for (const id of ids) {
    const m = /^lr-(\d+)$/.exec(id);
    if (!m) continue;
    const lrId = Number(m[1]);
    const lr = legRuns.find((r) => r.id === lrId);
    if (lr?.shipment_order_id != null) set.add(lr.shipment_order_id);
  }
  return Array.from(set);
}
